import { SbomToolSchema, ToolSchema, type SbomTool, type Tool } from './api-client/api-schemas'
import { getRuntimeEnv } from './runtime-env'

export type ScaTool = Extract<Tool, SbomTool>

/**
 * DAST (Dynamic Application Security Testing) tools scan a running application and
 * report findings by attacked URL rather than by source file. OWASP ZAP is the first.
 */
export const DAST_TOOLS = ['zap'] as const satisfies readonly Tool[]
export type DastTool = (typeof DAST_TOOLS)[number]

/**
 * Pixee's own AI scanner generates findings from the repository itself — no external account, no
 * integration, and nothing to import (ENH-590). Listed as a set so a second AI scanner has one
 * place to join.
 */
export const AI_SCANNER_TOOLS = ['pixee_ai'] as const satisfies readonly Tool[]
export type AiScannerTool = (typeof AI_SCANNER_TOOLS)[number]

/**
 * Freeform upload takes any file and extracts findings from it with an LLM, rather than parsing one
 * tool's format (ENH-599). Its findings arrive through the Organization page, not per repository.
 */
export const FREEFORM_TOOLS = ['freeform'] as const satisfies readonly Tool[]
export type FreeformTool = (typeof FREEFORM_TOOLS)[number]

/** Tools made by another vendor, as opposed to Pixee's own AI scanner and freeform upload. */
export type ThirdPartyTool = Exclude<Tool, AiScannerTool | FreeformTool>

export type SastTool = Exclude<Tool, ScaTool | DastTool>

/**
 * Determines if the given tool supports SBOM (Software Bill of Materials) uploads.
 * SBOM tools can have additional archive files uploaded alongside scan results.
 *
 * @param tool - The tool to check
 * @returns true if the tool supports SBOM uploads, false otherwise
 */
export function isSbomTool(tool: Tool): tool is ScaTool {
  const { success } = SbomToolSchema.safeParse(tool)

  return success
}

/**
 * Determines if the given tool is an SCA (Software Composition Analysis) tool.
 * SCA tools analyze dependencies and third-party libraries for security vulnerabilities.
 *
 * @param tool - The tool to check
 * @returns true if the tool is an SCA tool, false otherwise
 */
export function isScaTool(tool: Tool): tool is ScaTool {
  return isSbomTool(tool)
}

/**
 * Determines if the given tool is a DAST (Dynamic Application Security Testing) tool.
 * DAST tools scan a running application and locate findings by attacked URL/endpoint
 * rather than by source file.
 *
 * @param tool - The tool to check
 * @returns true if the tool is a DAST tool, false otherwise
 */
export function isDastTool(tool: Tool): tool is DastTool {
  return (DAST_TOOLS as readonly Tool[]).includes(tool)
}

/**
 * Determines if the given tool is a SAST (Static Application Security Testing) tool.
 * SAST tools analyze source code for security vulnerabilities and code quality issues.
 *
 * @param tool - The tool to check
 * @returns true if the tool is a SAST tool, false otherwise
 */
export function isSastTool(tool: Tool): tool is SastTool {
  return !isScaTool(tool) && !isDastTool(tool)
}

/**
 * Determines if the given tool is one of Pixee's AI scanners, which generate findings in-platform
 * rather than importing them from an external tool.
 *
 * @param tool - The tool to check
 * @returns true if the tool is an AI scanner, false otherwise
 */
export function isAiScannerTool(tool: Tool): tool is AiScannerTool {
  return (AI_SCANNER_TOOLS as readonly Tool[]).includes(tool)
}

/**
 * Determines if the given tool is freeform upload, which ingests arbitrary files rather than one
 * tool's report format.
 *
 * @param tool - The tool to check
 * @returns true if the tool is freeform upload, false otherwise
 */
export function isFreeformTool(tool: Tool): tool is FreeformTool {
  return (FREEFORM_TOOLS as readonly Tool[]).includes(tool)
}

/**
 * The tools a user may pick, with the deploy's runtime flags applied — the single place that
 * decides tool visibility, so a new picker can't forget one.
 *
 * Pass an ordered subset (e.g. a display order) to keep that order; the default is schema order.
 * The AI scanners and freeform upload are Labs features: with Labs off, they are absent everywhere.
 */
export function getSelectableTools(tools: readonly Tool[] = ToolSchema.options): Tool[] {
  const { isLabsEnabled } = getRuntimeEnv()

  return tools.filter(tool => isLabsEnabled || !(isAiScannerTool(tool) || isFreeformTool(tool)))
}

/**
 * The tools a workflow may be configured against. A scheduled or PR workflow pulls results from an
 * external tool, so the AI scanners — which have neither an integration to pull from nor an import
 * path — are never offered here, flag or no flag.
 */
export function getWorkflowTools(tools: readonly Tool[] = ToolSchema.options): Tool[] {
  return getSelectableTools(tools).filter(tool => !isAiScannerTool(tool))
}
